import React from 'react';
import { Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { IBookingLimitRule } from 'types/bookingLimit';
import { formatResetAt, formatSpent } from './helpers';
import s from './s.module.less';

interface Props {
    /** 已保存的规则（不是草稿）——汇总带反映的是当前生效的状态 */
    rule?: IBookingLimitRule;
    /** practice owner 的时区，未必是当前用户的 */
    timezone: string;
    /** 本周期已占用的新患者数；practice 视图传合计人数 */
    bookings: number;
    weeklySpentCents: number;
    /** practice 视图下第一格标签换成「Limit per provider」，强调不是共享池 */
    isPractice: boolean;
}

const bookingsTip = (isPractice: boolean) =>
    isPractice
        ? 'New patients who booked any provider at the practice through the Klarity Health marketplace since Monday. Patients they already see, and bookings made outside the marketplace, are not included.'
        : 'New patients who booked you through the Klarity Health marketplace since Monday. Counted when they book, not by appointment date. Patients you already see, and bookings made outside the marketplace, are not included.';

/**
 * 产品 2026-09-17 给的文案，两处按确认做了调整：
 *
 *  1. 计数的名词用「new marketplace patients」而不是「marketplace bookings」——
 *     传进来的 count 就是左边那格的新患者数，两者差约 9%（prod 某周 1978 vs 1801），
 *     说成 bookings 会和旁边的标签自相矛盾。
 *     「The fee for each booking」这句保留 booking：它讲的是计费机制本身
 *     （每笔收费预约一份费用），不是在报数。
 *  2. 「Your invoice is the final number.」只对个人视图输出（原型 :773 的
 *     `pr ? '' : ...`）—— practice 视图看的是全 practice 汇总，不对应一张账单。
 *
 * 刻意说明「不是统一单价」：lead gen fee 随患者所在州与专科变化
 * （prod 近 90 天单笔 $80–$120，均值约 $97），原型里那句
 * 「N bookings at $120 per visit」按固定单价反推是错的。
 */
const spentTip = (count: number, isPractice: boolean): string => {
    const noun = `new marketplace patient${count === 1 ? '' : 's'}`;
    const invoice = isPractice ? '' : ' Your invoice is the final number.';
    return `Lead-gen fees for ${count} ${noun} so far this week. The fee for each booking depends on the patient's state and the specialty, so it is not a flat rate. Pro-Listing charges are not included.${invoice}`;
};

const Help = ({ title }: { title: string }) => (
    <Tooltip title={title}>
        <QuestionCircleOutlined style={{ color: '#9ca3af', fontSize: 13 }} />
    </Tooltip>
);

const SummaryBand: React.FC<Props> = ({ rule, timezone, bookings, weeklySpentCents, isPractice }) => {
    const on = !!rule?.enabled;

    return (
        <div className={s.band}>
            <div className={s.bandCell}>
                <div className={s.bandLabel}>{isPractice ? 'Limit per provider' : 'Limit'}</div>
                <div className={s.bandValue}>
                    {on && rule?.limitCount ? `${rule.limitCount} a week` : 'None'}
                </div>
            </div>

            <div className={s.bandCell}>
                <div className={s.bandLabel}>
                    New marketplace patients this week
                    <Help title={bookingsTip(isPractice)} />
                </div>
                <div className={s.bandValue}>{bookings}</div>
            </div>

            <div className={s.bandCell}>
                <div className={s.bandLabel}>
                    Spent this week
                    <Help title={spentTip(bookings, isPractice)} />
                </div>
                <div className={s.bandValue}>{formatSpent(weeklySpentCents)}</div>
            </div>

            {/* 限制关闭时没有「下次重置」可言，整格不渲染 */}
            {on && rule && (
                <div className={s.bandCell}>
                    <div className={s.bandLabel}>Next reset</div>
                    <div className={s.bandValue}>{formatResetAt(rule.periodEndAt, timezone)}</div>
                </div>
            )}
        </div>
    );
};

export default SummaryBand;
